import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RefreshCw } from "lucide-react";
import { Panel, PanelTitle } from "@/components/panels";
import { useSessionPages } from "@/components/sessions/useSessionPages";
import { dateLabel, number } from "@/components/sessions/sessionData";
import { ModelUsageBody } from "./AlloyParts";
import { creditUnit } from "./alloyView";
import PersonCard from "./PersonCard";
import { bySource, groupByPerson, initialsOf, openablePeople, percentLabel } from "./usageByPerson";
import "./usageByPerson.css";

// Amounts print through `unit` (creditUnit): Alloy tokens for a brand on tokens, dollars otherwise.
const DOLLARS = creditUnit();

// The Alloy page's Usage panel for workspaces with usage by person switched on: By person, By model (the table
// the page always had), By source. The numbers are the Sessions page's two inventories, loaded the same way
// (useSessionPages, ten pages at a time, then "Load more"), grouped by the member behind each dashboard chat and,
// where the Slack people route can name them, the person who started each Slack conversation.

const TABS = [["people", "By person"], ["models", "By model"], ["sources", "By source"]];

function ShareCell({ item }) {
  return <td className={`r tnum ${item.share == null ? "quiet" : "pct"}`}>{percentLabel(item.share)}</td>;
}

function UsageRow({ item, onOpen, group = false, unit = DOLLARS }) {
  const used = item.sessions.length > 0;
  const former = item.person?.status === "revoked";
  const inSlack = !group && item.channels?.Slack > 0;
  const subline = item.kind === "person"
    ? `${item.person.role.charAt(0).toUpperCase()}${item.person.role.slice(1)} · ${item.person.email}${former ? " · Former member" : ""}${used || !item.person.you ? "" : " · No conversations yet"}`
    : item.kind === "unknown"
      ? "A dashboard sign-in that is no longer on the team"
      : item.kind === "slack"
        ? "In Slack · not on the dashboard team"
        : item.note;
  const label = <>{item.name}{item.person?.you ? <span className="quiet"> (you)</span> : null}</>;
  return (
    <tr data-testid={group ? "alloy-usage-group" : item.kind === "slack" ? "alloy-slack-person-row" : "alloy-people-row"}>
      <td>
        <div className="pp-person">
          <span className={`pp-avatar ${group ? "group" : former ? "former" : ""}`} aria-hidden="true">{group ? item.name.charAt(0) : initialsOf(item.name)}</span>
          <div>
            <div className="pp-name">
              {used && onOpen ? (
                <button type="button" className="pp-open" aria-haspopup="dialog" onClick={(event) => onOpen(item.key, event.currentTarget)}>{label}</button>
              ) : (
                <b>{label}</b>
              )}
              {inSlack ? <span className="pp-tag" data-testid="alloy-slack-tag">Slack</span> : null}
            </div>
            <small>{subline}</small>
          </div>
        </div>
        {item.share != null ? (
          <div className="pp-mini" role="img" aria-label={`${percentLabel(item.share)} of recorded ${unit.noun}`}><i className={group ? "group" : ""} style={{ width: `${Math.max(1, Math.min(100, item.share))}%` }} /></div>
        ) : null}
      </td>
      <td className={`r tnum ${used ? "" : "quiet"}`}>{used ? number(item.sessions.length) : "—"}</td>
      <ShareCell item={item} />
      <td className={`r tnum ${item.recordedSessions ? "" : "quiet"}`}>{item.recordedSessions ? <strong>{unit.recorded(item.knownCostUsd)}</strong> : "—"}</td>
    </tr>
  );
}

function SourceRow({ item, unit = DOLLARS }) {
  const used = item.sessions.length > 0;
  return (
    <tr data-testid="alloy-source-row">
      <td>
        <b>{item.name}</b>
        {item.share != null ? <div className="pp-mini" role="img" aria-label={`${percentLabel(item.share)} of recorded ${unit.noun}`}><i style={{ width: `${Math.max(1, Math.min(100, item.share))}%` }} /></div> : null}
      </td>
      <td className={`r tnum ${used ? "" : "quiet"}`}>{used ? number(item.sessions.length) : "—"}</td>
      <ShareCell item={item} />
      <td className={`r tnum ${item.recordedSessions ? "" : "quiet"}`}>{item.recordedSessions ? <strong>{unit.recorded(item.knownCostUsd)}</strong> : "—"}</td>
    </tr>
  );
}

function Progress({ provider, history, grouped }) {
  const loading = provider.loading || history.loading;
  const done = provider.done && history.done;
  const error = provider.error || history.error;
  const pages = provider.pages.length + history.pages.length;
  if (error) {
    return (
      <div className="pp-error" role="alert">
        <span>Some history could not be loaded. Recorded amounts cover what did load.</span>
        <button type="button" onClick={() => { if (provider.error) provider.load(); if (history.error) history.load(); }}>Try again</button>
      </div>
    );
  }
  return (
    <p className="pp-coverage" aria-live="polite">
      {loading ? `Loading history… ${number(pages)} ${pages === 1 ? "page" : "pages"} so far. ` : done ? "" : "History is still partial. "}
      {grouped.coverage ? `Recorded costs since ${new Date(grouped.coverage.since).toLocaleDateString()}, through ${dateLabel(grouped.coverage.asOf)}, for the current connection. ` : ""}
      {grouped.slack.matched
        ? `Dashboard chats are private, so only your own show here. Slack conversations are matched to the person who started them, by their Slack email. A thread counts for whoever started it. ${grouped.slack.complete ? "" : "Some Slack people could not be looked up, so their conversations stay under Slack. "}Scheduled jobs and direct API calls are not assigned to people.`
        : "Dashboard chats are private, so only your own show here. Slack, scheduled jobs and direct API calls are not assigned to people."}
      {" "}A dash means unavailable, never zero.
    </p>
  );
}

export default function PeoplePanel({ api, clientSlug, people = [], routing, configured, keyReady, onRefreshPeople, unit = DOLLARS }) {
  const provider = useSessionPages(api, clientSlug, "getAgentConversations");
  const history = useSessionPages(api, clientSlug, "getAgentConversationHistory");
  const [tab, setTab] = useState("people");
  const [selectedKey, setSelectedKey] = useState(null);
  const tabRefs = useRef([]);
  const openerRef = useRef(null);
  // Who started each Slack conversation. Optional: until it answers, or if it cannot, Slack stays one group.
  const [slack, setSlack] = useState(null);
  const slackRequest = useRef(0);
  const loadSlack = useCallback(() => {
    const request = ++slackRequest.current;
    if (typeof api?.getAlloySlackPeople !== "function") return;
    Promise.resolve()
      .then(() => api.getAlloySlackPeople())
      .then(
        (page) => { if (slackRequest.current === request) setSlack(page?.available === true ? page : null); },
        () => { if (slackRequest.current === request) setSlack(null); },
      );
  }, [api]);
  useEffect(() => {
    setSlack(null);
    loadSlack();
    return () => { slackRequest.current += 1; };
  }, [loadSlack, clientSlug]);
  const openPerson = (key, opener) => {
    openerRef.current = opener ?? null;
    setSelectedKey(key);
  };
  const grouped = useMemo(() => groupByPerson({ providerPages: provider.pages, historyPages: history.pages, people, slack }), [provider.pages, history.pages, people, slack]);
  const sources = useMemo(() => bySource(grouped), [grouped]);
  const selected = selectedKey ? openablePeople(grouped).find((item) => item.key === selectedKey) || null : null;
  const loading = provider.loading || history.loading;
  const done = provider.done && history.done;
  const used = grouped.people.filter((item) => item.sessions.length).length;

  const refresh = () => {
    provider.refresh();
    history.refresh();
    loadSlack();
    onRefreshPeople?.();
  };
  const loadMore = () => {
    if (!provider.done) provider.load();
    if (!history.done) history.load();
  };
  const onTabKey = (event, index) => {
    const next = event.key === "ArrowRight" ? (index + 1) % TABS.length : event.key === "ArrowLeft" ? (index - 1 + TABS.length) % TABS.length : event.key === "Home" ? 0 : event.key === "End" ? TABS.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    setTab(TABS[next][0]);
    tabRefs.current[next]?.focus();
  };

  return (
    <Panel data-testid="alloy-people" aria-labelledby="alloy-people-title">
      <PanelTitle id="alloy-people-title" aside={
        <button type="button" onClick={refresh} disabled={loading} aria-label="Refresh usage" className="inline-flex min-h-[32px] items-center gap-1.5 rounded-lg px-1.5 text-xs font-medium text-sm-ink/65 hover:text-sm-ink disabled:opacity-50">
          <RefreshCw size={13} aria-hidden="true" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      }>Usage breakdown</PanelTitle>
      <p className="mt-1 text-xs leading-relaxed text-sm-ink/65">Recorded {unit.name} by team member, model and source.</p>

      <div className="pp-tabs" role="tablist" aria-label="Usage breakdown">
        {TABS.map(([id, label], index) => (
          <button
            type="button"
            role="tab"
            key={id}
            id={`alloy-usage-tab-${id}`}
            data-testid={`alloy-usage-tab-${id}`}
            ref={(node) => { tabRefs.current[index] = node; }}
            aria-selected={tab === id}
            aria-controls={`alloy-usage-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
            onKeyDown={(event) => onTabKey(event, index)}
          >
            {label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id="alloy-usage-panel-people" aria-labelledby="alloy-usage-tab-people" tabIndex={0} hidden={tab !== "people"}>
        <div className="pp-note"><span>All loaded history · recorded {unit.name}</span><span>Open a name for their card</span></div>
        <table className="pp-table">
          <caption className="sr-only">Recorded {unit.name} by team member. Open a name for that person's card.</caption>
          <thead><tr><th style={{ width: "52%" }}>Team member</th><th className="r" style={{ width: "16%" }}>Conversations</th><th className="r" style={{ width: "14%" }}>Share</th><th className="r">{unit.tokens ? "Tokens" : "Credits"}</th></tr></thead>
          <tbody>
            {grouped.people.map((item) => <UsageRow key={item.key} item={item} onOpen={openPerson} unit={unit} />)}
            {grouped.slackPeople.map((item) => <UsageRow key={item.key} item={item} onOpen={openPerson} unit={unit} />)}
            {grouped.unknownPeople.map((item) => <UsageRow key={item.key} item={item} onOpen={openPerson} unit={unit} />)}
            {grouped.groups.map((item) => <UsageRow key={item.key} item={item} group unit={unit} />)}
            {grouped.unassigned ? (
              <tr data-testid="alloy-usage-unassigned">
                <td><b>Not tied to a conversation</b><small>Usage the provider recorded without a conversation id.</small></td>
                <td className="r quiet">—</td>
                <td className="r quiet">—</td>
                <td className={`r tnum ${grouped.unassigned.actualCostUsd == null ? "quiet" : ""}`}>{grouped.unassigned.actualCostUsd == null ? "—" : <strong>{unit.recorded(grouped.unassigned.actualCostUsd)}</strong>}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <div className="pp-foot">
          <span data-testid="alloy-people-note">{number(used)} of {number(grouped.people.length)} team members had conversations you can see{grouped.slackPeople.length ? ` · ${number(grouped.slackPeople.length)} more in Slack` : ""} · {unit.recorded(grouped.workspaceKnownCostUsd)}{unit.tokens ? " tokens" : ""} recorded in {number(grouped.loadedSessions)} loaded</span>
          {!done ? <button type="button" onClick={loadMore} disabled={loading}>{loading ? "Loading…" : "Load more"}</button> : <Link to="/agent/sessions">Every conversation on Sessions <ArrowRight size={12} aria-hidden="true" /></Link>}
        </div>
        <Progress provider={provider} history={history} grouped={grouped} />
      </div>

      <div role="tabpanel" id="alloy-usage-panel-models" aria-labelledby="alloy-usage-tab-models" tabIndex={0} hidden={tab !== "models"}>
        <div data-testid="alloy-model-usage"><ModelUsageBody routing={routing} configured={configured} keyReady={keyReady} unit={unit} /></div>
      </div>

      <div role="tabpanel" id="alloy-usage-panel-sources" aria-labelledby="alloy-usage-tab-sources" tabIndex={0} hidden={tab !== "sources"}>
        <div className="pp-note"><span>All loaded history · where the conversations came from</span></div>
        <table className="pp-table">
          <caption className="sr-only">Recorded {unit.name} by conversation source.</caption>
          <thead><tr><th style={{ width: "52%" }}>Source</th><th className="r" style={{ width: "16%" }}>Conversations</th><th className="r" style={{ width: "14%" }}>Share</th><th className="r">{unit.tokens ? "Tokens" : "Credits"}</th></tr></thead>
          <tbody>{sources.map((item) => <SourceRow key={item.key} item={item} unit={unit} />)}</tbody>
        </table>
        <Progress provider={provider} history={history} grouped={grouped} />
      </div>

      <PersonCard
        person={selected}
        open={Boolean(selected)}
        onOpenChange={(next) => { if (!next) setSelectedKey(null); }}
        workspaceKnownCostUsd={grouped.workspaceKnownCostUsd}
        slackMatched={grouped.slack.matched}
        recentWindow={grouped.recentWindow}
        coverage={grouped.coverage}
        loading={loading}
        onRefresh={refresh}
        returnFocusTo={openerRef}
        unit={unit}
      />
    </Panel>
  );
}
